import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { ROLE_LABEL, TIER_LABEL } from '@/lib/supabase'
import { Badge } from '@/components/ui'
import { FEATURES } from '@/lib/features'
import { KtomEndorsement } from '@/components/KtomEndorsement'

interface NavItem {
  to: string
  label: string
  show: (ctx: ReturnType<typeof useAuth>) => boolean
}

const NAV: NavItem[] = [
  { to: '/', label: 'Home', show: () => true },
  { to: '/library', label: 'Resources', show: (c) => c.hasPortalAccess },
  { to: '/evaluation', label: 'Evaluation', show: (c) => FEATURES.evaluation && c.hasPortalAccess && !c.isKtm },
  { to: '/reports', label: 'Your results', show: (c) => FEATURES.reports && c.hasPortalAccess && !c.isKtm },
  { to: '/orders', label: 'Orders', show: (c) => FEATURES.orders && c.hasPortalAccess && !c.isKtm },
  { to: '/team', label: 'Your team', show: (c) => c.isOrgAdmin },
  { to: '/admin', label: 'Administration', show: (c) => c.isKtm },
]

export function AppShell() {
  const auth = useAuth()
  const { profile, signOut } = auth
  const org = profile?.organisations

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:bg-surface focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      <header className="on-dark bg-pounamu text-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Beige tile: the logo is transparent outside its blob and loses
                letters on Deep Green (see AuthLayout). */}
            <div className="shrink-0 rounded-xl bg-paper p-1.5">
              <img src="/aunty-logo.png" alt="Kōrero Pūtea with Aunty Āwhi" className="h-11 w-auto" />
            </div>
            <div>
              <p className="font-display text-base leading-tight text-white">
                Kōrero Pūtea with Aunty Āwhi <span className="text-teal">partner portal</span>
              </p>
              <p className="text-xs text-white/75">
                {org ? org.name : 'Ki Tua o Matariki'}
                {org && (
                  <>
                    {' · '}
                    {TIER_LABEL[org.tier]}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            {org?.subscription_status === 'grace' && <Badge tone="alert">Renewal overdue</Badge>}
            <span className="hidden text-white/80 sm:inline">
              {profile?.full_name ?? profile?.email}
            </span>
            <button onClick={signOut} className="text-white/80 underline underline-offset-4 hover:text-white">
              Sign out
            </button>
          </div>
        </div>

        <nav aria-label="Portal sections" className="mx-auto max-w-6xl px-4">
          <ul className="flex flex-wrap gap-x-5 gap-y-1 pb-1 text-sm">
            {NAV.filter((item) => item.show(auth)).map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `inline-block border-b-2 pb-2 pt-1 ${
                      isActive
                        ? 'border-teal font-medium text-white'
                        : 'border-transparent text-white/75 hover:text-white'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Ngāti Paoa Moana border along the foot of the header. */}
        <div aria-hidden="true" className="moana-h h-5 text-teal/80" />
      </header>

      <main id="main" className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-6xl px-4 pb-10 text-xs text-inksoft">
        <div className="space-y-4 border-t border-line pt-6">
          <p>
            Signed in as {ROLE_LABEL[profile?.role ?? 'facilitator']}
            {profile?.auth_method === 'azure' && ' via Microsoft 365'}. Resources in this portal are
            licensed to your organisation for delivering the programme.
          </p>
          <KtomEndorsement />
        </div>
      </footer>
    </div>
  )
}
